import { useCallback, useEffect, useRef, useState } from "react";

// The Web Speech API is absent from this TypeScript version's DOM lib and is
// still prefixed in Chromium. Declaring the handful of members we actually use
// beats taking on @types/dom-speech-recognition for a prototype.
interface SpeechAlternative {
  transcript: string;
}
interface SpeechResult {
  isFinal: boolean;
  0: SpeechAlternative;
}
interface SpeechResultList {
  length: number;
  [index: number]: SpeechResult;
}
interface SpeechResultEvent {
  resultIndex: number;
  results: SpeechResultList;
}
interface SpeechErrorEvent {
  error: string;
}
interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  onresult: ((event: SpeechResultEvent) => void) | null;
  onerror: ((event: SpeechErrorEvent) => void) | null;
  onend: (() => void) | null;
}
type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getRecognitionCtor(): SpeechRecognitionCtor | undefined {
  if (typeof window === "undefined") return undefined;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition;
}

// Only the errors a user can act on get a message; "no-speech" and "aborted"
// are routine and would be noise.
const ERROR_MESSAGES: Record<string, string> = {
  "not-allowed": "Microphone access was denied. Enable it in your browser settings.",
  "service-not-allowed": "Microphone access was denied. Enable it in your browser settings.",
  "audio-capture": "No microphone was found.",
  network: "Speech recognition lost its connection.",
};

interface UseSpeechInputOptions {
  value: string;
  onChange: (next: string) => void;
}

export function useSpeechInput({ value, onChange }: UseSpeechInputOptions) {
  const [isListening, setIsListening] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  // Whatever was in the textarea when dictation started (or was last
  // re-baselined onto a user edit, see onresult below). Transcripts are
  // appended to this snapshot, so interim results that get revised mid-sentence
  // replace the tail rather than stacking up.
  const baseTextRef = useRef("");
  // The exact string this hook last wrote to the textarea (base + final +
  // interim). Used to detect when the user has typed since our last write —
  // see onresult.
  const lastWrittenRef = useRef("");

  // Read through refs so the callbacks stay stable across every keystroke.
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const isSupported = getRecognitionCtor() !== undefined;

  const stop = useCallback(() => {
    recognitionRef.current?.stop();
  }, []);

  const start = useCallback(() => {
    const Ctor = getRecognitionCtor();
    if (!Ctor || recognitionRef.current) return;

    const recognition = new Ctor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = "en-US";

    const existing = valueRef.current;
    baseTextRef.current =
      existing && !existing.endsWith(" ") ? `${existing} ` : existing;
    // The textarea currently holds exactly `existing` — record that as our
    // "last write" so the first onresult call doesn't mistake the untouched
    // starting value for a user edit.
    lastWrittenRef.current = existing;

    let finalText = "";
    recognition.onresult = (event) => {
      // The user may have edited the textarea since our last write (the
      // field stays enabled while dictating). Detect that by comparing
      // against the exact string we last wrote — base + final + interim,
      // not just a final-only baseline — so a live interim revision from
      // the recognizer (which repeats an unfinished phrase across events)
      // never itself looks like an edit; only a value that actually changed
      // out from under us does. On a real edit, re-baseline onto it and
      // drop whatever final transcript had accumulated so it doesn't get
      // glued on ahead of the user's typing.
      if (valueRef.current !== lastWrittenRef.current) {
        const edited = valueRef.current;
        baseTextRef.current =
          edited && !edited.endsWith(" ") ? `${edited} ` : edited;
        finalText = "";
      }

      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (result.isFinal) finalText += result[0].transcript;
        else interim += result[0].transcript;
      }
      const nextValue = baseTextRef.current + finalText + interim;
      lastWrittenRef.current = nextValue;
      onChangeRef.current(nextValue);
    };

    recognition.onerror = (event) => {
      setError(ERROR_MESSAGES[event.error] ?? null);
      // Reset to idle here rather than waiting on `onend` — some engines
      // don't fire it after every error (permission denial in particular),
      // which would otherwise strand the button on "listening" forever. If
      // `onend` does still fire afterward, it repeats the same assignments
      // below and is a harmless no-op.
      recognitionRef.current = null;
      setIsListening(false);
    };

    recognition.onend = () => {
      // Idempotent: onerror may already have reset these.
      recognitionRef.current = null;
      setIsListening(false);
    };

    setError(null);
    recognitionRef.current = recognition;
    setIsListening(true);
    recognition.start();
  }, []);

  const toggle = useCallback(() => {
    if (recognitionRef.current) stop();
    else start();
  }, [start, stop]);

  useEffect(() => () => recognitionRef.current?.stop(), []);

  return { isSupported, isListening, error, toggle };
}
